import type { AssignmentBuilderSchema, AssignmentBuilderSchemaInput } from '@repo/schemas/assignment/assignmentBuilder';
import type { UseFormReturn } from 'react-hook-form';

import { useCallback, useEffect, useRef, useState } from 'react';

import { assignmentBuilderDraftStore } from './assignmentBuilderDraftStore';

interface UseAssignmentBuilderDraftArgs {
  draftKey: string;
  form: UseFormReturn<AssignmentBuilderSchemaInput, unknown, AssignmentBuilderSchema>;
  emptyValues: Partial<AssignmentBuilderSchemaInput>;
  /** Drafts are only kept in create mode; edit mode's source of truth is remote. */
  enabled: boolean;
}

interface UseAssignmentBuilderDraftResult {
  attachments: File[];
  setAttachments: (files: File[]) => void;
  isDraftReady: boolean;
  clearDraft: () => void;
}

export function useAssignmentBuilderDraft(args: UseAssignmentBuilderDraftArgs): UseAssignmentBuilderDraftResult {
  const { draftKey, form, emptyValues, enabled } = args;

  const [attachments, setAttachmentsState] = useState<File[]>([]);
  const [isDraftReady, setIsDraftReady] = useState<boolean>(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Debounced write of the whole record (form values + attachments) under one key.
  const persist = useCallback(
    (nextAttachments: File[]) => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        assignmentBuilderDraftStore
          .set(draftKey, {
            values: form.getValues(),
            attachments: nextAttachments,
            updatedAt: Date.now(),
          })
          .catch(() => undefined);
      }, 300);
    },
    [draftKey, form]
  );

  // Restore once before the form/editor render, so the values seed Tiptap's
  // initial content and the attachments seed the file selector.
  useEffect(() => {
    if (!enabled) {
      setIsDraftReady(true);
      return;
    }
    let cancelled = false;
    assignmentBuilderDraftStore.prune((record) => record.updatedAt < Date.now() - 7 * 24 * 60 * 60 * 1000).catch(() => undefined);

    assignmentBuilderDraftStore
      .get(draftKey)
      .then((record) => {
        if (cancelled || !record) return;
        form.reset({ ...emptyValues, ...record.values });
        setAttachmentsState(record.attachments);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setIsDraftReady(true);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutRef.current);
    };
  }, [draftKey, form, emptyValues, enabled]);

  // Persist edits once restoration has completed.
  useEffect(() => {
    if (!enabled || !isDraftReady) return;
    const unsubscribe = form.subscribe({
      formState: { values: true },
      callback: () => persist(attachments),
    });
    return () => unsubscribe();
  }, [form, persist, attachments, isDraftReady, enabled]);

  const setAttachments = useCallback(
    (files: File[]) => {
      setAttachmentsState(files);
      if (enabled && isDraftReady) persist(files);
    },
    [persist, enabled, isDraftReady]
  );

  const clearDraft = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setAttachmentsState([]);
    assignmentBuilderDraftStore.delete(draftKey).catch(() => undefined);
  }, [draftKey]);

  return { attachments, setAttachments, isDraftReady, clearDraft };
}
